import { useEffect, useState } from "react"
import type { CombinedError } from "urql"
import { useQuery } from "urql"

import { LocationBaseListDocument } from "@/graphql/codegen/graphql"
import type { Location, LocationListFilter, LocationQueryListArgs, Query } from "@/graphql/codegen/graphql"

export interface UseGetBaseLocationsReturn {
  locations: Location[]
  isFetchingLocations: boolean
  locationsError: CombinedError | undefined
  loadMoreLocations: () => void
  getLocationsWithReset: () => void
  resetLocations: () => void
}

const LIMIT = 20

export const useGetBaseLocations = (filter: LocationListFilter = {}): UseGetBaseLocationsReturn => {
  const [baseLocations, setBaseLocations] = useState<Location[]>([])
  const [offset, setOffset] = useState(0)
  const [hasNextPage, setHasNextPage] = useState(true)

  const [{ data, fetching, error }, reexecuteQuery] = useQuery<Pick<Query, "location">, LocationQueryListArgs>({
    query: LocationBaseListDocument,
    variables: { input: { filter, limit: LIMIT, offset } },
    pause: !filter.companyId,
  })

  useEffect(() => {
    if (data?.location.list.__typename === "LocationListSuccess") {
      const paginatedLocations = data.location.list.locations
      setBaseLocations((prev: Location[]) => (offset === 0 ? paginatedLocations : [...prev, ...paginatedLocations]))
      if (paginatedLocations.length < LIMIT) {
        setHasNextPage(false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const loadMoreLocations = () => {
    if (hasNextPage) {
      setOffset((prevOffset) => prevOffset + LIMIT)
    }
  }

  const resetLocations = () => {
    setOffset(0)
    setBaseLocations([])
    setHasNextPage(true)
  }

  const getLocationsWithReset = () => {
    resetLocations()
    reexecuteQuery({ requestPolicy: "network-only" })
  }

  return {
    locations: baseLocations,
    isFetchingLocations: fetching,
    locationsError: error,
    loadMoreLocations,
    getLocationsWithReset,
    resetLocations,
  }
}
